// Funciones puras que leen los datos ya cargados por la página y devuelven texto +
// severidad. Ningún string de insight/acción se escribe a mano en un componente —
// si el umbral no se cumple, ese insight/acción directamente no aparece.
import { UMBRALES } from './tokens';
import { franjasConSobreturnos, ocupacionMediaFranjas } from './calculosReportes';

// Conversión global ponderada (no promedio simple de los % por tipo — pesa por volumen).
export function conversionGlobal(conversionPorTipo) {
    if (!conversionPorTipo?.length) return null;
    const totalTurnos = conversionPorTipo.reduce((acc, c) => acc + (c.totalTurnos || 0), 0);
    const conTramite = conversionPorTipo.reduce((acc, c) => acc + (c.conTramite || 0), 0);
    if (totalTurnos === 0) return null;
    return (conTramite / totalTurnos) * 100;
}

function severidadDesdeNivel(nivel) {
    return nivel === 'rojo' ? 'bad' : nivel === 'ambar' ? 'warn' : 'info';
}

// nivel de un valor "más alto = mejor" (efectividad, conversión, ocupación-min)
function nivelDirecto(valor, bueno, alerta) {
    if (valor >= bueno) return 'verde';
    if (valor >= alerta) return 'ambar';
    return 'rojo';
}

// nivel de un valor "más bajo = mejor" (ausentismo, espera)
function nivelInverso(valor, bueno, alerta) {
    if (valor <= bueno) return 'verde';
    if (valor <= alerta) return 'ambar';
    return 'rojo';
}

/**
 * @param {object} data - { resumen, porHorario, ausentismoPorCanal, conversionPorTipo,
 *   motivosNoAvance, capacidad }
 * @returns {Array<{title: string, text: string, severity: 'bad'|'warn'|'info'}>} máx. 3, ordenados por severidad
 */
export function calcularInsights(data) {
    const { resumen, porHorario, motivosNoAvance, capacidad } = data;
    const candidatos = [];

    // Expresado como asistencia y no como ausentismo: son el mismo dato al derecho y al revés
    // (ambos sobre los turnos ya resueltos, suman 100%), y el estudio sigue un solo número.
    if (resumen?.tasaAsistencia != null) {
        const v = Number(resumen.tasaAsistencia);
        const nivel = nivelDirecto(v, UMBRALES.efectividad.bueno, UMBRALES.efectividad.alerta);
        if (nivel !== 'verde') {
            candidatos.push({
                title: 'Asistencia por debajo de lo esperado',
                text: `Se presentó el ${v.toFixed(1)}% de los turnos que ya se resolvieron en el período — meta ${UMBRALES.efectividad.bueno}%.`,
                severity: severidadDesdeNivel(nivel),
            });
        }
    }

    // "Sobre capacidad" es lo que el propio sistema marca como sobreturno: un turno que excede el cupo
    // de su franja ese día. No se deduce de los totales del período (que suman todos los días).
    const conSobreturnos = franjasConSobreturnos(porHorario);
    if (conSobreturnos.length > 0) {
        const sobreturnos = conSobreturnos.reduce((acc, f) => acc + (f.sobreturnos || 0), 0);
        const cupo = capacidad ? ` de los ${capacidad} turnos que soporta cada franja por día` : ' del cupo de cada franja';
        candidatos.push({
            title: `${conSobreturnos.length} franja(s) con sobreturnos`,
            text: `Se atendieron ${sobreturnos} sobreturno(s) por encima${cupo} — ahí es donde se generan esperas.`,
            severity: conSobreturnos.length >= 3 ? 'bad' : 'warn',
        });
    }

    const conversion = conversionGlobal(data.conversionPorTipo);
    if (conversion != null) {
        const nivel = nivelDirecto(conversion, UMBRALES.conversionPct.bueno, UMBRALES.conversionPct.alerta);
        if (nivel !== 'verde') {
            candidatos.push({
                title: 'Conversión a trámite baja',
                text: `Solo en el ${conversion.toFixed(1)}% de las atenciones finalizadas se abrió un trámite — meta ${UMBRALES.conversionPct.bueno}%.`,
                severity: severidadDesdeNivel(nivel),
            });
        }
    }

    if (resumen?.esperaMediaMinutos != null) {
        const v = Number(resumen.esperaMediaMinutos);
        const nivel = nivelInverso(v, UMBRALES.esperaMin.bueno, UMBRALES.esperaMin.alerta);
        if (nivel !== 'verde') {
            candidatos.push({
                title: 'Espera en sala por encima de la meta',
                text: `La espera media es de ${v.toFixed(0)} min — meta ${UMBRALES.esperaMin.bueno} min.`,
                severity: severidadDesdeNivel(nivel),
            });
        }
    }

    const totalMotivos = (motivosNoAvance || []).reduce((acc, m) => acc + (m.cantidad || 0), 0);
    const otro = (motivosNoAvance || []).find((m) => m.motivo === 'Otro');
    if (otro && totalMotivos >= UMBRALES.muestraMinima) {
        const pctOtro = (otro.cantidad / totalMotivos) * 100;
        if (pctOtro >= 25) {
            candidatos.push({
                title: '"Otro" domina los motivos de no avance',
                text: `El ${pctOtro.toFixed(0)}% de los motivos registrados es texto libre sin clasificar — dato ciego para actuar.`,
                severity: pctOtro >= 40 ? 'bad' : 'warn',
            });
        }
    }

    const orden = { bad: 0, warn: 1, info: 2 };
    return candidatos.sort((a, b) => orden[a.severity] - orden[b.severity]).slice(0, 3);
}

/**
 * @param {object} data - { resumen, porHorario, dias, conversionPorTipo, capacidad } — `dias` son los
 *   días del período con turnos (calculosReportes.diasConTurnos).
 * @returns {Array<{title: string, text: string, rule: string, severity: 'bad'|'warn'|'info'}>}
 */
export function calcularAcciones(data) {
    const { resumen, porHorario, dias, capacidad } = data;
    const acciones = [];

    if (resumen?.tasaAsistencia != null) {
        const v = Number(resumen.tasaAsistencia);
        if (v < UMBRALES.efectividad.bueno) {
            const critico = v < UMBRALES.efectividad.alerta;
            acciones.push({
                title: critico ? 'Asistencia crítica' : 'Asistencia por debajo de la meta',
                text: critico
                    ? 'De los turnos que ya se resolvieron, la proporción en que la persona vino cayó fuerte — evaluar un recordatorio de WhatsApp más cercano al turno, o doble confirmación para los canales que más faltan.'
                    : 'Por debajo de la meta de asistencia — reforzar el recordatorio del día anterior y la confirmación del turno.',
                rule: `asistencia < ${critico ? UMBRALES.efectividad.alerta : UMBRALES.efectividad.bueno}%`,
                severity: critico ? 'bad' : 'warn',
            });
        }
    }

    // La acción de ausentismo se fusionó con la de asistencia de arriba: al ser complementarias
    // saltaban siempre juntas y decían lo mismo (reforzar recordatorios) en dos tarjetas.

    if (resumen?.esperaMediaMinutos != null) {
        const v = Number(resumen.esperaMediaMinutos);
        if (v > UMBRALES.esperaMin.bueno) {
            const critico = v > UMBRALES.esperaMin.alerta;
            acciones.push({
                title: critico ? 'Espera en sala crítica' : 'Espera en sala por encima de la meta',
                text: 'Revisar dotación en las franjas con más espera y si la duración de atención configurada por trámite es realista.',
                rule: `espera > ${critico ? UMBRALES.esperaMin.alerta : UMBRALES.esperaMin.bueno} min`,
                severity: critico ? 'bad' : 'warn',
            });
        }
    }

    // Ocupación por día: la capacidad es de una franja en un día, no del período entero.
    const ocupacionMedia = ocupacionMediaFranjas(porHorario, capacidad, dias);
    if (ocupacionMedia != null) {
        if (ocupacionMedia > UMBRALES.ocupacionPct.bueno[1]) {
            const critico = ocupacionMedia > UMBRALES.ocupacionPct.alerta;
            acciones.push({
                title: critico ? 'Ocupación por encima de la capacidad' : 'Ocupación media alta',
                text: 'Evaluar sumar franjas u operadores en los horarios pico antes de que el sobreturno se vuelva la norma.',
                rule: `ocupación media ${critico ? '>' : '>'} ${critico ? UMBRALES.ocupacionPct.alerta : UMBRALES.ocupacionPct.bueno[1]}%`,
                severity: critico ? 'bad' : 'warn',
            });
        }
    }

    const conversion = conversionGlobal(data.conversionPorTipo);
    const totalTurnos = (data.conversionPorTipo || []).reduce((acc, c) => acc + (c.totalTurnos || 0), 0);
    if (conversion != null && totalTurnos >= 40 && conversion < UMBRALES.conversionPct.bueno) {
        const critico = conversion < UMBRALES.conversionPct.alerta;
        acciones.push({
            title: critico ? 'Conversión a trámite crítica' : 'Conversión a trámite por debajo de la meta',
            text: 'Mucho volumen de turnos con poca conversión suele ser demanda mal calificada al agendar — reforzar el filtro en recepción/chatbot.',
            rule: `conversión ${critico ? '<' : '<'} ${critico ? UMBRALES.conversionPct.alerta : UMBRALES.conversionPct.bueno}% y n > 40`,
            severity: critico ? 'bad' : 'warn',
        });
    }

    const orden = { bad: 0, warn: 1, info: 2 };
    return acciones.sort((a, b) => orden[a.severity] - orden[b.severity]);
}
